import { supabase } from "@/api/supabaseClient";

export type CommentMention = {
  profile_id: string;
  display_name: string | null;
  roles: string[];
};

export type EntryComment = {
  id: string;
  organization_id: string;
  case_id: string;
  entry_id: string;
  author_id: string;
  author_display_name: string | null;
  author_roles: string[];
  body: string | null;
  edited_at: string | null;
  withdrawn_at: string | null;
  withdrawn_by: string | null;
  client_comment_id: string | null;
  created_at: string;
  mentions: CommentMention[];
};

export type MentionableMember = {
  profile_id: string;
  display_name: string | null;
  roles: string[];
};

function newId(): string {
  try {
    if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  } catch {
    /* ignore */
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export class CommentsApi {
  static newClientCommentId(): string {
    return newId();
  }

  static async listForCase(caseId: string): Promise<EntryComment[]> {
    const { data, error } = await supabase.rpc("list_entry_comments_for_case", {
      p_case_id: caseId,
    });
    if (error) throw error;
    return (data ?? []) as EntryComment[];
  }

  static async listForEntry(entryId: string): Promise<EntryComment[]> {
    const { data, error } = await supabase.rpc("list_entry_comments", {
      p_entry_id: entryId,
    });
    if (error) throw error;
    return (data ?? []) as EntryComment[];
  }

  static async listMentionable(caseId: string): Promise<MentionableMember[]> {
    const { data, error } = await supabase.rpc("list_mentionable_members", {
      p_case_id: caseId,
    });
    if (error) throw error;
    return (data ?? []) as MentionableMember[];
  }

  static async add(input: {
    entryId: string;
    body: string;
    mentionedProfileIds?: string[];
    clientCommentId?: string;
  }): Promise<EntryComment> {
    const { data, error } = await supabase.rpc("add_entry_comment", {
      p_entry_id: input.entryId,
      p_client_comment_id: input.clientCommentId ?? newId(),
      p_body: input.body,
      p_mentioned_profile_ids: input.mentionedProfileIds ?? [],
    });
    if (error) throw error;
    return data as EntryComment;
  }

  static async edit(input: {
    commentId: string;
    body: string;
    mentionedProfileIds?: string[];
  }): Promise<EntryComment> {
    const { data, error } = await supabase.rpc("edit_entry_comment", {
      p_comment_id: input.commentId,
      p_body: input.body,
      p_mentioned_profile_ids: input.mentionedProfileIds ?? [],
    });
    if (error) throw error;
    return data as EntryComment;
  }

  static async withdraw(commentId: string): Promise<EntryComment> {
    const { data, error } = await supabase.rpc("withdraw_entry_comment", {
      p_comment_id: commentId,
    });
    if (error) throw error;
    return data as EntryComment;
  }
}
